import { cn } from '@goaltree/ui/lib/utils'
import { ChevronDown, ChevronRight, Flag, Flame, X } from 'lucide-react'
import { type ReactNode, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { StrengthChip, StrengthRing, useHabitStrength } from '@/components/habit-strength'
import { useHoldToWater } from '@/components/hold-to-water'
import { type HabitStats, habitStats } from '@/components/inbox-habits'
import { StreakShare } from '@/components/share-card'
import { StreakFlame } from '@/components/streak-icons'
import { useHabitCheckStyle } from '@/hooks/use-habit-check-style'
import { CATEGORY_COLORS, categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { celebrateFrom } from '@/lib/celebrate'
import { logicalDayStr } from '@/lib/day'
import { flameTipSeen, markFlameTipSeen } from '@/lib/first-run'
import { useHabitAccent } from '@/lib/habit-accent'

// Category as the leading item of a row's meta line: coloured dot + name.
export function CategoryMeta({ category }: { category: string }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const base = CATEGORY_COLORS[category.toLowerCase()] ?? '#6b7280'
  return (
    <span
      className="flex shrink-0 items-center gap-1.5 font-semibold"
      style={{ color: categoryTextColor(base) }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: fill(base) }} />
      {t(`goals.cat.${category.toLowerCase()}`)}
    </span>
  )
}

// End of a habit row's grid: last-30-day completion (daily-type cadences) and the
// current streak when there is one. Nothing at all for a brand-new habit.
export function HabitRowSummary({
  completions,
  period,
  recursDays,
  createdAt,
  habitId,
}: {
  completions: number[]
  period?: string | null
  recursDays?: readonly number[] | null
  createdAt?: number
  // Makes the streak tappable to share.
  habitId?: string
}) {
  const { last30: rate, now } = habitStats(completions, period, createdAt, recursDays)
  const s = useHabitStrength(habitId)
  // Strength stands in for the 30-day rate here; the rate stays in the stats line.
  const strength = s && s.strength > 0 ? s.strength : null
  const last30 = strength === null ? rate : null
  if (last30 === null && now === 0 && strength === null) return null
  return (
    <span className="shrink-0 whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">
      {last30 !== null && <b className="font-bold text-foreground">{last30}%</b>}
      {last30 !== null && now > 0 && ' · '}
      {now > 0 &&
        (habitId ? (
          <StreakShare habitId={habitId} streak={now}>
            <StreakFlame />
            {now}
          </StreakShare>
        ) : (
          <>
            <StreakFlame />
            {now}
          </>
        ))}
      {strength !== null && (
        <>
          {now > 0 && ' · '}
          <StrengthChip value={strength} className="font-bold text-foreground" />
        </>
      )}
    </span>
  )
}

export function HistoryToggle({
  open = false,
  onClick,
  className = 'ml-auto',
}: {
  open?: boolean
  onClick: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn(
        className,
        'flex shrink-0 cursor-pointer items-center gap-0.5 whitespace-nowrap font-mono text-[11px] text-muted-foreground/70 transition-colors hover:text-primary-text',
      )}
    >
      {t('inbox.history')}
      {open ? (
        <ChevronDown className="h-3 w-3" strokeWidth={2.2} />
      ) : (
        <ChevronRight className="h-3 w-3" strokeWidth={2.2} />
      )}
    </button>
  )
}

// Summary above an expanded habit history.
export function HabitStatsLine({
  stats,
  toggle,
  habitId,
}: {
  stats: HabitStats
  toggle?: ReactNode
  habitId?: string
}) {
  const { t } = useTranslation()
  const strength = useHabitStrength(habitId)
  const accent = useHabitAccent(habitId)
  const n = (v: number | string) => <b className="font-bold text-foreground">{v}</b>
  const streak = (
    <>
      <StreakFlame /> {n(stats.now)} {t('inbox.statsNow')}
    </>
  )
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-muted-foreground">
      {habitId && stats.now > 0 ? (
        <StreakShare habitId={habitId} streak={stats.now} className="gap-1">
          {streak}
        </StreakShare>
      ) : (
        <span>{streak}</span>
      )}
      <span>
        {t('inbox.statsBest')} {n(stats.best)}
      </span>
      {strength && strength.strength > 0 && (
        <span className="inline-flex items-center gap-1">
          <StrengthRing value={strength.strength} color={accent} />
          {n(`${strength.strength}%`)} {t('strength.stat')}
        </span>
      )}
      <span>
        {n(stats.done)} {t('inbox.statsDone')}
      </span>
      {stats.last30 !== null && (
        <span>
          {n(`${stats.last30}%`)} {t('inbox.statsLast30')}
        </span>
      )}
      {toggle}
    </div>
  )
}

// Urgent marker on a task (never shown on habits). `superUrgent` swaps the flag
// for two overlapping red dots, the second ringed in the card colour.
export function UrgentFlag({
  className,
  title,
  superUrgent,
}: {
  className?: string
  title?: string
  superUrgent?: boolean
}) {
  const { t } = useTranslation()
  if (superUrgent) {
    const label = title ?? t('filter.prioritySuperUrgent')
    const flag = 'size-3.5 fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400'
    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        className={cn('relative inline-flex h-3.5 w-5 shrink-0', className)}
      >
        <Flag className={cn(flag, 'absolute top-0 left-0 opacity-60')} />
        <Flag className={cn(flag, 'absolute top-0 left-1.5')} />
      </span>
    )
  }
  return (
    <Flag
      aria-label={title}
      className={cn(
        'h-3.5 w-3.5 shrink-0 fill-red-500 text-red-500 dark:fill-red-400 dark:text-red-400',
        className,
      )}
      strokeWidth={2}
    >
      {title && <title>{title}</title>}
    </Flag>
  )
}

const flameTipListeners = new Set<() => void>()
// In-memory too, so × still works when localStorage is blocked.
let flameTipDismissed = false
function dismissFlameTip() {
  if (flameTipDismissed) return
  flameTipDismissed = true
  markFlameTipSeen()
  flameTipListeners.forEach((l) => l())
}
function useFlameTipVisible() {
  return useSyncExternalStore(
    (cb) => {
      flameTipListeners.add(cb)
      return () => flameTipListeners.delete(cb)
    },
    () => !flameTipDismissed && !flameTipSeen(),
    () => false,
  )
}

// A habit's daily check-in, in the gutter where tasks have their status circle.
export function HabitFlameCheck({
  done,
  onToggle,
  dim = false,
  inProgress = false,
}: {
  done: boolean
  onToggle: () => void
  dim?: boolean
  inProgress?: boolean
}) {
  const { t } = useTranslation()
  const [pop, setPop] = useState(false)
  const hold = useHabitCheckStyle() === 'hold'
  const btnRef = useRef<HTMLButtonElement>(null)
  const check = () => {
    if (!done) {
      setPop(true)
      dismissFlameTip()
      celebrateFrom(btnRef.current)
    }
    onToggle()
  }
  const water = useHoldToWater({
    enabled: hold && !done,
    onComplete: check,
    lift: { icon: Flame, fill: 'fill-orange-500 text-orange-500' },
  })
  const markDone = hold ? `${t('inbox.markDone')} · ${t('holdToWater.hint')}` : t('inbox.markDone')
  const label = done
    ? t('inbox.flameUndo')
    : inProgress
      ? `${t('goals.inProgress')} · ${markDone} (Space)`
      : `${markDone} (Space)`
  return (
    <button
      ref={btnRef}
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={label}
      title={label}
      {...water.handlers}
      onClick={(e) => {
        e.stopPropagation()
        if (!water.swallowClick()) check()
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className="group relative -my-1.5 -mr-1.5 -ml-1 shrink-0 cursor-pointer rounded-full p-1.5 outline-none transition-colors select-none [-webkit-touch-callout:none] hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60"
    >
      {water.water}
      <span
        onAnimationEnd={() => setPop(false)}
        className={cn('relative flex h-5 w-5 items-center justify-center', pop && 'flame-pop')}
      >
        {done ? (
          <Flame
            className="h-[18px] w-[18px] fill-orange-500 text-orange-500 drop-shadow-[0_0_3px_rgb(249_115_22/0.45)]"
            strokeWidth={1.8}
          />
        ) : inProgress ? (
          // Catching: an orange outline with the lower half lit.
          <span className="relative flex h-[18px] w-[18px]">
            <Flame className="absolute inset-0 h-full w-full text-orange-500" strokeWidth={1.8} />
            <Flame
              className="flame-flicker absolute inset-0 h-full w-full fill-orange-500/80 text-transparent"
              strokeWidth={1.8}
            />
          </span>
        ) : (
          // Same flame as the checked state, just unlit — checking fills it in.
          <Flame
            className={cn(
              'h-[18px] w-[18px] text-muted-foreground/50 transition-colors group-hover:text-orange-500/80',
              dim && 'opacity-50',
            )}
            strokeWidth={1.8}
          />
        )}
      </span>
    </button>
  )
}

// One-time hint on the first habit row; gone after the first check-in or ×.
export function FlameTip() {
  const { t } = useTranslation()
  const hold = useHabitCheckStyle() === 'hold'
  if (!useFlameTipVisible()) return null
  return (
    <div className="mt-1 flex items-center gap-1 font-sans text-[11px] font-medium text-primary-text">
      <Flame className="h-3 w-3 shrink-0" strokeWidth={2.2} />
      {t(hold ? 'holdToWater.flameTip' : 'inbox.flameTip')}
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={(e) => {
          e.stopPropagation()
          dismissFlameTip()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="-my-1 ml-0.5 cursor-pointer rounded p-1 text-muted-foreground/70 transition-colors hover:text-foreground"
      >
        <X className="h-3 w-3" strokeWidth={2.5} />
      </button>
    </div>
  )
}

// Latest completion in today's logical day, for the "Done ✓ 8:12am" meta.
export function doneTodayAt(completions: number[]): number | undefined {
  const today = logicalDayStr(Date.now())
  let at: number | undefined
  for (const ms of completions) if (logicalDayStr(ms) === today && (at ?? 0) < ms) at = ms
  return at
}

export function HabitDoneAt({ at }: { at: number }) {
  const { t, i18n } = useTranslation()
  const time = new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' })
    .format(at)
    .replace(/\s?([AP]M)$/i, (_, m: string) => m.toLowerCase())
  return (
    <span className="shrink-0 leading-none text-muted-foreground/70">
      {t('inbox.doneAt', { time })}
    </span>
  )
}

// Fill today's dot during the tap hold, before the completion is sent.
export function withPendingToday(completions: number[], pending: boolean): number[] {
  return pending && doneTodayAt(completions) === undefined
    ? [...completions, Date.now()]
    : completions
}
